import { ApolloClient, InMemoryCache } from '@apollo/client';
import { ApolloLink } from '@apollo/client/link/core/ApolloLink';
import { from as mergeLinks } from '@apollo/client/link/core/from';
import { RetryLink } from 'apollo-link-retry';
import { createUploadLink } from 'apollo-upload-client';
import { ApiHeaders } from '@people-analytix/shared';
import { getSelectedOrganizationIdEncr } from 'UI/lib';
import useAvailableLanguages from 'ui/customHooks/useAvailableLanguages';

if (typeof URLSearchParams !== 'function') {
  import('url-search-params-polyfill');
}

const createApolloClientInstance = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const availableLanguages = useAvailableLanguages();
  const defaultLang = availableLanguages.find(language => language.isDefault).languageCode;

  const middlewareLink = new ApolloLink((operation, forward) => {
    const headers = {
      [ApiHeaders.Context.Language.key]: localStorage.getItem('i18nextLng') || defaultLang,
      [ApiHeaders.Context.UserToken.key]: localStorage.getItem('palyxToken') || '',
      [ApiHeaders.Context.User.key]: localStorage.getItem('palyxUserId') || '',
    };
    let organizationId;

    const userId = localStorage.getItem('palyxUserId');
    organizationId = getSelectedOrganizationIdEncr(userId);

    if (organizationId) {
      headers[ApiHeaders.Context.Organization.key] = organizationId;
    }

    headers[ApiHeaders.Context.Tenant.key] = window.rootOrganizationId;

    operation.setContext({
      headers,
    });

    return forward(operation);
  });

  const authLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(response => {
      const context = operation.getContext();
      const {
        response: { headers },
      } = context;

      if (headers) {
        const headersToken = headers.get(ApiHeaders.Context.UserToken.key);
        const headersUserId = headers.get(ApiHeaders.Context.User.key);
        if (headersToken) {
          localStorage.setItem('palyxToken', headersToken);
        }
        if (headersUserId) {
          localStorage.setItem('palyxUserId', headersUserId);
        }
      }

      return response;
    });
  });

  const uploadLink = createUploadLink({
    uri: `${process.env.BACKEND_URL}/graphql`,
    fetch,
    credentials: 'include',
    headers: {
      'Apollo-Require-Preflight': 'true',
    },
  });

  const retryLink = new RetryLink({
    attempts: {
      max: 0,
    },
  });

  const inMemoryCache = new InMemoryCache({
    addTypename: true,
  });

  return new ApolloClient({
    link: mergeLinks([authLink, retryLink, middlewareLink, uploadLink]),
    cache: inMemoryCache.restore(window.__APOLLO_CLIENT__),
    shouldBatch: false,
  });
};

export default (() => {
  let instance = null;
  return () => {
    if (!instance) {
      instance = createApolloClientInstance();
    }
    return instance;
  };
})();
